/**
 * i18next bootstrap (ENG-MULTILOCALE-FIRST-CLASS Track I; catalogs lazy since
 * ENG-APP-UX-TUNEUP Track F, 2026-10-15).
 *
 * Catalogs are LLM-GENERATED per locale from the English base and
 * committed at build time (`output/generate_ui_catalogs.py`) — the same
 * zero-marginal-cost principle as Language Policy Packs; no per-language
 * hand-maintained string files (docs/product/experience-and-ui.md
 * § Settings → UI language). English is the base catalog and MUST stay
 * complete; missing keys in a generated catalog fall back to English.
 *
 * Side-effect module: imported once from `main.tsx` before the app
 * renders so `useTranslation()` never sees an uninitialized instance.
 *
 * **Payload (floor row 10).** Only the English base ships in the initial
 * payload. The other catalogs (~350 KB of JSON each) are registered as
 * loaders below and fetched on demand — `main.tsx` awaits
 * `ensureUiCatalog(initialLanguage)` before the first render so a German or
 * Spanish user never sees an English flash, and `setUiLanguage` loads the
 * catalog before switching. `verify_i18n_locale_parity` derives the shipped
 * set from the loader map, so a catalog that can reach a user is checked.
 *
 * As of 2026-08-12 all three catalogs are populated and at full key parity
 * (the parity verify is the gate), so all three are offered. **Adding a key
 * is therefore a THREE-file edit.** Only `en.json` is type-checked
 * (`i18next.d.ts` types `t()` off it alone); an English-only addition builds
 * green and silently ships the raw key path to Spanish and German users.
 * Either hand-translate into `es-419.json` + `de-DE.json` in the same
 * commit, or regenerate them with `output/generate_ui_catalogs.py`.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { getStoredUiLanguage, setStoredUiLanguage } from "../lib/session";
import { UI_LANGUAGES, resolveUiLanguage, type UiLanguage } from "./resolve";
import en from "./locales/en.json";

type Catalog = Record<string, unknown>;

/**
 * The lazily shipped catalogs — one loader per registered locale. The
 * registration site the parity verify reads: every catalog wired here is
 * checked the moment it can reach a user.
 */
const UI_CATALOG_LOADERS: Record<
  Exclude<UiLanguage, "en">,
  () => Promise<Catalog>
> = {
  "es-419": () =>
    import("./locales/es-419.json").then((m) => m.default as Catalog),
  "de-DE": () =>
    import("./locales/de-DE.json").then((m) => m.default as Catalog),
};

/**
 * The languages offerable this build — English plus every registered
 * loader. Population is guaranteed by `verify_i18n_locale_parity` (key-for-key
 * with `en.json`), so the switcher on `/settings/account` lists all three.
 */
export const AVAILABLE_UI_LANGUAGES: readonly UiLanguage[] =
  UI_LANGUAGES.filter((lang) => lang === "en" || lang in UI_CATALOG_LOADERS);

const loaded = new Set<UiLanguage>(["en"]);

/** Fetch and register a catalog once; a no-op for English and for repeats. */
export async function ensureUiCatalog(language: UiLanguage): Promise<void> {
  if (loaded.has(language)) return;
  const loader = UI_CATALOG_LOADERS[language as Exclude<UiLanguage, "en">];
  if (!loader) return;
  const catalog = await loader();
  i18n.addResourceBundle(language, "translation", catalog, true, true);
  loaded.add(language);
}

export const initialLanguage: UiLanguage = resolveUiLanguage(
  getStoredUiLanguage(),
  typeof navigator === "undefined"
    ? []
    : (navigator.languages ?? [navigator.language]),
  AVAILABLE_UI_LANGUAGES,
);

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
  },
  // The non-English catalog is added by `ensureUiCatalog` before the first
  // render (main.tsx awaits it) — not a partial bundle at runtime.
  partialBundledLanguages: true,
  lng: initialLanguage,
  fallbackLng: "en",
  interpolation: {
    // React already escapes rendered output.
    escapeValue: false,
  },
  returnEmptyString: false,
});

i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
});
if (typeof document !== "undefined") {
  document.documentElement.lang = initialLanguage;
}

/**
 * Switch the product-UI language and persist the per-user preference
 * (the /settings/account selector's single entry point). The catalog is
 * loaded before the switch so the page never renders raw keys.
 */
export function setUiLanguage(language: UiLanguage): void {
  setStoredUiLanguage(language);
  void ensureUiCatalog(language).then(() => i18n.changeLanguage(language));
}

export default i18n;
